import { observer } from "mobx-react-lite";
import { Badge } from "primereact/badge";
import { BreadCrumb } from "primereact/breadcrumb";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Menu } from "primereact/menu";
import { Sidebar } from "primereact/sidebar";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactECharts from "echarts-for-react";
import CompoundEvolutionAddNew from "../../../../app/common/CompoundEvolutionTimeline/CompoundEvolutionAddNew/CompoundEvolutionAddNew";
import SectionHeading from "../../../../app/common/SectionHeading/SectionHeading";
import SmilesView from "../../../../app/common/SmilesView/SmilesView";
import SmilesDrawer from "smiles-drawer";
import "./ProjectCompounds.css";
import { RootStoreContext } from "../../../../app/stores/rootStore";
import agent from "../../../../app/api/agent";

const ASSAY_OPTIONS = [
  { label: "IC50", value: "IC50" },
  { label: "Cytotoxicity", value: "Cytotoxicity" },
  { label: "Binding", value: "Binding" },
  { label: "Antimicrobial", value: "Antimicrobial" },
  { label: "Enzymatic", value: "Enzymatic" },
  { label: "Functional", value: "Functional" },
  { label: "Other", value: "Other" },
];

const ASSAY_TYPE_OPTIONS = [
  { label: "MIC", value: "MIC" },
  { label: "IC50", value: "IC50" },
  { label: "CC50", value: "CC50" },
  { label: "EC50", value: "EC50" },
  { label: "GI50", value: "GI50" },
  { label: "SI (Selectivity Index)", value: "SI" },
  { label: "Ki", value: "Ki" },
  { label: "Kd", value: "Kd" },
  { label: "MBC", value: "MBC" },
  { label: "MFC", value: "MFC" },
  { label: "Other (custom)", value: "__other__" },
];

/**
 * Returns a color severity for a numeric MIC/IC50 value.
 * Lower is better (more potent) → green.
 */
const getActivitySeverity = (assayName, value) => {
  if (value == null || value === "") return null;
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  const name = (assayName || "").toUpperCase();

  // MIC thresholds (µg/mL): ≤1 potent, 1-8 moderate, 8-32 weak, >32 inactive
  if (name === "MIC") {
    if (num <= 1) return "success";
    if (num <= 8) return "info";
    if (num <= 32) return "warning";
    return "danger";
  }
  // IC50 thresholds (µM): ≤0.1 potent, 0.1-1 good, 1-10 moderate, >10 weak
  if (name === "IC50") {
    if (num <= 0.1) return "success";
    if (num <= 1) return "info";
    if (num <= 10) return "warning";
    return "danger";
  }
  // CC50: higher is better (less toxic)
  if (name === "CC50") {
    if (num >= 100) return "success";
    if (num >= 30) return "info";
    if (num >= 10) return "warning";
    return "danger";
  }
  return null;
};

const getActivityBadgeLabel = (severity) => {
  switch (severity) {
    case "success": return "Potent";
    case "info": return "Active";
    case "warning": return "Weak";
    case "danger": return "Inactive";
    default: return null;
  }
};

// ─── ECharts color palette ───────────────────────────────────────
const CHART_COLORS = {
  success: "#22c55e",
  info: "#3b82f6",
  warning: "#eab308",
  danger: "#ef4444",
};

const getChartColor = (assayName, value) => {
  const sev = getActivitySeverity(assayName, value);
  return CHART_COLORS[sev] || "#6b7280";
};

const getChartLabel = (assayName, value) => {
  const sev = getActivitySeverity(assayName, value);
  const label = getActivityBadgeLabel(sev);
  return label || "";
};

/**
 * Build ECharts bar chart option from compound activities.
 */
const buildActivityChartOption = (activities) => {
  if (!activities || activities.length === 0) return {};

  const names = activities.map((a) => a.assayName || "?");
  const values = activities.map((a) => {
    const v = parseFloat(a.value);
    return isNaN(v) ? 0 : v;
  });
  const units = activities.map((a) => a.unit || "");
  const colors = activities.map((a) => getChartColor(a.assayName, a.value));
  const labels = activities.map((a) => getChartLabel(a.assayName, a.value));

  return {
    tooltip: {
      trigger: "axis",
      formatter: (params) => {
        const p = params[0];
        const idx = p.dataIndex;
        const a = activities[idx];
        return `<strong>${a.assayName || "?"}</strong><br/>
          Value: <strong>${p.value}</strong> ${a.unit || ""}<br/>
          ${a.assayType ? `Type: ${a.assayType}<br/>` : ""}
          ${labels[idx] ? `Status: ${labels[idx]}` : ""}`;
      },
    },
    grid: {
      left: 40,
      right: 20,
      top: 10,
      bottom: 30,
    },
    xAxis: {
      type: "category",
      data: names,
      axisLabel: { fontWeight: "bold", fontSize: 11 },
    },
    yAxis: {
      type: "value",
      name: units.length > 0 && [...new Set(units)].length === 1 ? units[0] : "",
      nameTextStyle: { fontSize: 10, color: "#888" },
    },
    series: [
      {
        type: "bar",
        data: values.map((v, i) => ({
          value: v,
          itemStyle: { color: colors[i], borderRadius: [4, 4, 0, 0] },
        })),
        barWidth: 30,
        label: {
          show: true,
          position: "top",
          formatter: (p) => {
            const idx = p.dataIndex;
            const a = activities[idx];
            return `${p.value}${a.unit ? " " + a.unit : ""}`;
          },
          fontSize: 10,
          color: "#666",
        },
      },
    ],
  };
};

// ─── Skeleton Loading Component ───────────────────────────────────

const SkeletonTable = () => (
  <div className="flex flex-column w-full fadein animation-duration-500">
    <div className="flex w-full align-items-center justify-content-between mb-3">
      <Skeleton width="200px" height="32px" />
      <Skeleton width="130px" height="32px" borderRadius="6px" />
    </div>
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex align-items-center gap-2 py-2" style={{ borderBottom: "1px solid #f0f0f0" }}>
        <Skeleton width="18px" height="18px" borderRadius="50%" />
        <Skeleton width="40px" height="40px" borderRadius="6px" />
        <Skeleton width="120px" height="16px" />
        <Skeleton width="140px" height="16px" className="ml-auto" />
        <Skeleton width="60px" height="16px" />
        <Skeleton width="40px" height="16px" />
        <Skeleton width="24px" height="24px" borderRadius="50%" />
      </div>
    ))}
  </div>
);

// ─── Property Card Component ──────────────────────────────────────

const PropertyCard = ({ compound }) => {
  if (!compound) return null;
  const props = [];
  if (compound.molWeight) props.push({ label: "MW", value: parseFloat(compound.molWeight).toFixed(1) });
  if (compound.molFormula) props.push({ label: "Formula", value: compound.molFormula });
  if (compound.molLogP) props.push({ label: "LogP", value: compound.molLogP });
  if (compound.molArea) props.push({ label: "Area", value: compound.molArea + (compound.molAreaUnit ? " " + compound.molAreaUnit : "") });
  if (compound.molVolume) props.push({ label: "Volume", value: compound.molVolume + (compound.molVolumeUnit ? " " + compound.molVolumeUnit : "") });

  if (props.length === 0) return null;

  return (
    <div style={{
      background: "linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)",
      border: "1px solid #e2e8f0",
      borderRadius: "10px",
      padding: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div className="flex align-items-center gap-1.5" style={{ marginBottom: "10px", paddingBottom: "6px", borderBottom: "1px solid #e8edf5" }}>
        <i className="pi pi-atom" style={{ fontSize: "11px", color: "#6366f1" }} />
        <span style={{ fontSize: "10px", fontWeight: 600, color: "#6366f1", letterSpacing: "0.4px", textTransform: "uppercase" }}>
          Properties
        </span>
      </div>
      <div className="grid" style={{ margin: 0, rowGap: "6px" }}>
        {props.map((p, i) => (
          <div key={p.label} className="col-6" style={{ padding: "0 4px" }}>
            <div style={{
              fontSize: "9px", color: "#94a3b8", lineHeight: 1.2, fontWeight: 500,
              textTransform: "uppercase", letterSpacing: "0.3px",
            }}>{p.label}</div>
            <div style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>{p.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Activity Chart Component ─────────────────────────────────────

const ActivityChart = ({ activities }) => {
  if (!activities || activities.length === 0) return null;
  const option = buildActivityChartOption(activities);

  return (
    <div style={{
      marginTop: "16px",
      padding: "16px",
      background: "linear-gradient(145deg, #ffffff 0%, #fafbfe 100%)",
      borderRadius: "10px",
      border: "1px solid #e8edf5",
      boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
    }}>
      <div className="flex align-items-center justify-content-between" style={{ marginBottom: "12px" }}>
        <div className="flex align-items-center gap-2">
          <div style={{
            width: 24, height: 24, borderRadius: 6,
            background: "linear-gradient(135deg, #6366f1, #4f46e5)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <i className="pi pi-chart-line" style={{ fontSize: "11px", color: "#fff" }} />
          </div>
          <div>
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#1e293b" }}>
              Activity Potency
            </span>
            <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: 1 }}>
              {activities.length} measurement{activities.length > 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </div>
      <ReactECharts
        option={option}
        style={{ height: "180px" }}
        notMerge
        lazyUpdate
      />
      <div className="flex gap-3 flex-wrap justify-content-center" style={{
        fontSize: "10px", color: "#94a3b8",
        marginTop: "8px", paddingTop: "8px",
        borderTop: "1px solid #f1f5f9",
      }}>
        <span><span style={{ color: "#22c55e" }}>●</span> Potent</span>
        <span><span style={{ color: "#3b82f6" }}>●</span> Active</span>
        <span><span style={{ color: "#eab308" }}>●</span> Weak</span>
        <span><span style={{ color: "#ef4444" }}>●</span> Inactive</span>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────

const ProjectCompounds = ({ id: projectId }) => {
  const rootStore = useContext(RootStoreContext);
  const compoundStore = rootStore.compoundStore;
  const { loadingProjectCompounds, projectCompounds, fetchCompoundsByProject, addCompoundActivity, editCompoundActivity, deleteCompoundActivity, deleteCompound } =
    compoundStore;
  const { selectedProject } = rootStore.projectStore;

  const [displayAddCompound, setDisplayAddCompound] = useState(false);
  const [displayAddActivity, setDisplayAddActivity] = useState(false);
  const [activeCompoundForActivity, setActiveCompoundForActivity] = useState(null);
  const [activityForm, setActivityForm] = useState({
    assayName: "",
    assayType: "",
    value: "",
    unit: "",
    method: "",
    condition: "",
    targetName: "",
    pmid: "",
    notes: "",
  });
  const [customAssayName, setCustomAssayName] = useState("");
  const [displayEditActivity, setDisplayEditActivity] = useState(false);
  const [editingActivityRow, setEditingActivityRow] = useState(null);
  const [displayDeleteConfirm, setDisplayDeleteConfirm] = useState(false);
  const [deletingActivityRow, setDeletingActivityRow] = useState(null);
  const [displayStructurePreview, setDisplayStructurePreview] = useState(false);
  const [structurePreviewSmiles, setStructurePreviewSmiles] = useState(null);
  const [rowHeight, setRowHeight] = useState(50);
  const toast = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const csvInputRef = useRef(null);
  const tableWrapperRef = useRef(null);
  const [tableScrollHeight, setTableScrollHeight] = useState("flex");
  const [displayColumnDialog, setDisplayColumnDialog] = useState(false);
  const [editingColumns, setEditingColumns] = useState([]);
  const [newColumnName, setNewColumnName] = useState("");
  const [selectedCompounds, setSelectedCompounds] = useState([]);
  const [batchMode, setBatchMode] = useState(false);
  const [showBatchDeleteDialog, setShowBatchDeleteDialog] = useState(false);
  const moreMenuRef = useRef(null);

  // ── DataSet state ──
  const { compoundDatasets } = compoundStore;
  const dsParam = searchParams.get("ds");
  const [activeDatasetId, setActiveDatasetId] = useState(dsParam || "all");

  // Sync URL ds param → activeDatasetId
  useEffect(() => {
    setActiveDatasetId(dsParam || "all");
  }, [dsParam]);

  // BreadCrumb
  const breadCrumbItems = [
    { label: "Project Management", command: () => navigate("/pm/") },
    { label: "Projects", command: () => navigate("/pm/project/") },
    { label: selectedProject?.projectName || "Project" },
    { label: "Compounds" },
  ];

  // ── Custom Columns ──
  const { compoundColumns, fetchColumns, saveColumns } = compoundStore;

  useEffect(() => {
    if (projectId) fetchColumns(projectId);
  }, [projectId, fetchColumns]);


  const openColumnDialog = () => {
    setEditingColumns(compoundColumns.map((c) => ({
      id: c.id,
      columnName: c.columnName,
      displayName: c.displayName,
      dataType: c.dataType || "string",
      sortOrder: c.sortOrder,
    })));
    setNewColumnName("");
    setDisplayColumnDialog(true);
  };

  const addCustomColumn = () => {
    if (!newColumnName.trim()) return;
    const colName = newColumnName.trim().toLowerCase().replace(/\s+/g, "_");
    setEditingColumns((prev) => [
      ...prev,
      { id: null, columnName: colName, displayName: newColumnName.trim(), dataType: "string", sortOrder: prev.length },
    ]);
    setNewColumnName("");
  };

  const removeCustomColumn = (index) => {
    setEditingColumns((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveColumns = async () => {
    const result = await saveColumns(projectId, editingColumns);
    if (result) {
      toast.current.show({ severity: "success", summary: "Columns saved" });
      setDisplayColumnDialog(false);
    } else {
      toast.current.show({ severity: "error", summary: "Failed to save columns" });
    }
  };



  // ── CSV Import ──
  const handleCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await agent.Compounds.importCsv(projectId, file);
      toast.current.show({ severity: "success", summary: "Imported", detail: `${result} rows imported` });
      fetchCompoundsByProject(projectId);
      fetchColumns(projectId);
    } catch (err) {
      toast.current.show({ severity: "error", summary: "Import failed", detail: err.message || "Unknown error" });
    }
    e.target.value = "";
  };

  useEffect(() => {
    fetchCompoundsByProject(projectId);
  }, [projectId, fetchCompoundsByProject]);

  // ── Inline editing ──
  const [localRows, setLocalRows] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const editSnapshot = useRef(null);

  // Sync projectCompounds → localRows, filtered by active dataset
  useEffect(() => {
    let filtered = projectCompounds;

    // Built-in "Synthesized" filter
    if (activeDatasetId === "synthesized") {
      filtered = (filtered || []).filter((c) => c.synthesized === "YES");
    } else {
      const ds = compoundDatasets.find((d) => d.id === activeDatasetId);
      if (ds && ds.filterJson && ds.filterJson !== "{}") {
        try {
          const f = JSON.parse(ds.filterJson);
          filtered = (filtered || []).filter((c) => {
            // ID* contains
            if (f.externalCompoundIds) {
              const idStr = c.externalCompoundIds
                ? (Array.isArray(c.externalCompoundIds)
                  ? c.externalCompoundIds.map((e) => e.externalId || e).join(" ")
                  : String(c.externalCompoundIds))
                : "";
              if (!idStr.toLowerCase().includes(String(f.externalCompoundIds).toLowerCase())) return false;
            }
            // SYNTHESIZED exact
            if (f.synthesized && c.synthesized !== f.synthesized) return false;
            // MW range
            if (f.molWeight) {
              const mw = parseFloat(c.molWeight);
              if (isNaN(mw)) return false;
              if (f.molWeight.min != null && mw < f.molWeight.min) return false;
              if (f.molWeight.max != null && mw > f.molWeight.max) return false;
            }
            // LogP range
            if (f.molLogP) {
              const lp = parseFloat(c.molLogP);
              if (isNaN(lp)) return false;
              if (f.molLogP.min != null && lp < f.molLogP.min) return false;
              if (f.molLogP.max != null && lp > f.molLogP.max) return false;
            }
            return true;
          });
        } catch (_) {}
      }
    }
    // Parse extraData into row.extra (like PatentCompounds pattern)
    setLocalRows(
      (filtered || []).map((c) => {
        let extra = {};
        try {
          extra = c.extraData ? JSON.parse(c.extraData) : {};
        } catch (_) {}
        return { ...c, extra };
      })
    );
    setSelectedCompounds([]);
  }, [projectCompounds, activeDatasetId, compoundDatasets]);

  // ── Inline editing functions ──
  const startEditing = (row, index) => {
    editSnapshot.current = JSON.parse(JSON.stringify(row));
    setEditingIndex(index);
  };

  const cancelEditing = () => {
    if (editSnapshot.current !== null && editingIndex !== null) {
      setLocalRows((prev) => {
        const updated = [...prev];
        updated[editingIndex] = editSnapshot.current;
        return updated;
      });
    }
    editSnapshot.current = null;
    setEditingIndex(null);
  };

  const updateField = (index, field, value) => {
    setLocalRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSaveRow = async (row, index) => {
    try {
      const payload = {
        smiles: row.smile || "",
        molWeight: row.molWeight || "",
        molArea: row.molArea || "",
        externalCompoundIds: typeof row.externalCompoundIds === "string"
          ? row.externalCompoundIds
          : Array.isArray(row.externalCompoundIds)
            ? row.externalCompoundIds.map((e) => e.externalId || e).join(", ")
            : "",
        commonName: row.commonName || "",
        synthesized: row.synthesized || "",
        entityId: row.entityId || "",
        synthesisId: row.synthesisId || "",
        designId: row.designId || "",
      };
      const res = await agent.Compounds.edit(row.id, payload);
      if (res) {
        toast.current.show({ severity: "success", summary: "Saved", detail: "Compound updated" });
        setEditingIndex(null);
        editSnapshot.current = null;
        fetchCompoundsByProject(projectId);
      } else {
        toast.current.show({ severity: "error", summary: "Error", detail: "Failed to save" });
      }
    } catch (err) {
      toast.current.show({ severity: "error", summary: "Error", detail: err.message || "Failed to save" });
    }
  };

  const handleDeleteRow = async (row, index) => {
    if (row.id) {
      const res = await deleteCompound(row.id);
      if (res !== null) {
        toast.current.show({ severity: "success", summary: "Deleted", detail: "Compound removed" });
        setEditingIndex(null);
        editSnapshot.current = null;
        fetchCompoundsByProject(projectId);
      } else {
        toast.current.show({ severity: "error", summary: "Error", detail: "Failed to delete" });
      }
    } else {
      // New row not yet saved — just remove from local list
      setLocalRows((prev) => prev.filter((_, i) => i !== index));
      setEditingIndex(null);
      editSnapshot.current = null;
    }
  };

  const handleBatchDelete = async () => {
    if (selectedCompounds.length === 0) return;
    try {
      const ids = selectedCompounds.map((c) => c.id);
      const res = await rootStore.compoundStore.batchDeleteCompounds(ids);
      if (res !== null) {
        toast.current.show({ severity: "success", summary: "Deleted", detail: `${res} compound(s) removed` });
        setSelectedCompounds([]);
        setBatchMode(false);
        setShowBatchDeleteDialog(false);
        fetchCompoundsByProject(projectId);
      } else {
        toast.current.show({ severity: "error", summary: "Error", detail: "Failed to delete compounds" });
      }
    } catch (err) {
      toast.current.show({ severity: "error", summary: "Error", detail: err.message || "Failed to delete" });
    }
  };

  const actionTemplate = (rowData, options) => {
    const idx = options.rowIndex;
    const isEditing = editingIndex === idx;
    return (
      <div className="flex gap-1">
        {isEditing ? (
          <>
            <Button
              icon="pi pi-check"
              className="p-button-rounded p-button-success p-button-text"
              tooltip="Save"
              onClick={() => handleSaveRow(rowData, idx)}
            />
            <Button
              icon="pi pi-trash"
              className="p-button-rounded p-button-danger p-button-text"
              tooltip="Delete"
              onClick={() => handleDeleteRow(rowData, idx)}
            />
            <Button
              icon="pi pi-times"
              className="p-button-rounded p-button-secondary p-button-text"
              tooltip="Cancel"
              onClick={cancelEditing}
            />
          </>
        ) : (
          <>
            <Button
              icon="pi pi-pencil"
              className="p-button-rounded p-button-info p-button-text"
              tooltip="Modify"
              onClick={() => startEditing(rowData, idx)}
            />
          </>
        )}
      </div>
    );
  };

  const openEditActivity = (activity) => {
    setEditingActivityRow(activity);
    setActivityForm({
      assayName: activity.assayName || '',
      assayType: activity.assayType || '',
      value: activity.value || '',
      unit: activity.unit || '',
      method: activity.method || '',
      condition: activity.condition || '',
      targetName: activity.targetName || '',
      pmid: activity.pmid || '',
      notes: activity.notes || '',
    });
    setCustomAssayName('');
    setDisplayEditActivity(true);
  };

  const openDeleteConfirm = (activity) => {
    setDeletingActivityRow(activity);
    setDisplayDeleteConfirm(true);
  };

  const resetActivityForm = () => {
    setActivityForm({
      assayName: "",
      assayType: "",
      value: "",
      unit: "",
      method: "",
      condition: "",
      targetName: "",
      pmid: "",
      notes: "",
    });
    setCustomAssayName("");
  };

  const openAddActivity = (compound) => {
    setActiveCompoundForActivity(compound);
    resetActivityForm();
    setDisplayAddActivity(true);
  };

  const handleEditActivity = async () => {
    const finalAssayName = activityForm.assayName === "__other__" ? customAssayName : activityForm.assayName;
    if (!finalAssayName || !activityForm.value) {
      toast.current.show({ severity: "warn", summary: "Validation", detail: "Assay name and value are required" });
      return;
    }
    const payload = { ...activityForm, assayName: finalAssayName, projectId };
    const res = await editCompoundActivity(editingActivityRow.id, payload);
    if (res !== null) {
      toast.current.show({ severity: "success", summary: "Success", detail: "Activity updated" });
      setDisplayEditActivity(false);
      setEditingActivityRow(null);
      fetchCompoundsByProject(projectId);
    } else {
      toast.current.show({ severity: "error", summary: "Error", detail: "Failed to update activity" });
    }
  };

  const handleDeleteActivity = async () => {
    if (!deletingActivityRow) return;
    const res = await deleteCompoundActivity(deletingActivityRow.id);
    if (res !== null) {
      toast.current.show({ severity: "success", summary: "Success", detail: "Activity deleted" });
      setDisplayDeleteConfirm(false);
      setDeletingActivityRow(null);
      fetchCompoundsByProject(projectId);
    } else {
      toast.current.show({ severity: "error", summary: "Error", detail: "Failed to delete activity" });
    }
  };

  const handleSubmitActivity = async () => {
    const finalAssayName = activityForm.assayName === "__other__" ? customAssayName : activityForm.assayName;
    if (!finalAssayName || !activityForm.value) {
      toast.current.show({ severity: "warn", summary: "Validation", detail: "Assay name and value are required" });
      return;
    }
    const payload = { ...activityForm, assayName: finalAssayName, projectId };
    const res = await addCompoundActivity(activeCompoundForActivity.id, payload);
    if (res !== null) {
      toast.current.show({ severity: "success", summary: "Success", detail: "Activity added" });
      setDisplayAddActivity(false);
      setActiveCompoundForActivity(null);
      fetchCompoundsByProject(projectId);
    } else {
      toast.current.show({ severity: "error", summary: "Error", detail: "Failed to add activity" });
    }
  };

  // ─── Column Templates ──────────────────────────────────────────

  const activityCountTemplate = (rowData) => {
    const count = (rowData.compoundActivities || []).length;
    if (count === 0) return <span style={{ fontSize: "11px", color: "#cbd5e1" }}>—</span>;
    const severity = count > 5 ? "success" : count > 0 ? "info" : null;
    return (
      <div className="flex align-items-center justify-content-center" style={{ gap: 4 }}>
        <Badge value={count} severity={severity} />
        <span style={{ fontSize: "10px", color: "#94a3b8" }}>{count === 1 ? "record" : "records"}</span>
      </div>
    );
  };

  // ── Redraw all structure canvases ───────────────────────────────
  const redrawAllCanvases = useCallback(() => {
    document.querySelectorAll(".patent-structure-canvas").forEach((el) => {
      const smiles = el.dataset.smiles;
      if (!smiles) return;
      const parent = el.parentElement;
      const cellWidth = parent?.offsetWidth || 200;
      const cellHeight = parent?.offsetHeight || 60;
      const w = Math.max(cellWidth - 16, 60);
      const h = Math.max(cellHeight - 4, 36);
      const dpr = window.devicePixelRatio || 1;
      el.width = w * dpr;
      el.height = h * dpr;
      el.style.width = w + "px";
      el.style.height = h + "px";
      try {
        const drawer = new SmilesDrawer.Drawer({
          width: w * dpr, height: h * dpr,
          bondThickness: 1.0, shortBondLength: 1.0,
          fontSizeScale: 0.8, atomVisualization: "default",
          isomericHighlight: true, explicitHydrogens: false,
        });
        SmilesDrawer.parse(
          smiles,
          (tree) => drawer.draw(tree, el.id, "light", false),
          (err) => console.error("SmilesDrawer error:", err)
        );
      } catch (e) {
        console.error("SmilesDrawer render error:", e);
      }
    });
  }, []);

  // Initial redraw after data loads and on rowHeight change
  useEffect(() => {
    if (projectCompounds.length > 0) {
      const t1 = setTimeout(() => redrawAllCanvases(), 100);
      const t2 = setTimeout(() => redrawAllCanvases(), 500);
      const t3 = setTimeout(() => redrawAllCanvases(), 1500);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [projectCompounds, rowHeight, redrawAllCanvases]);

  // ── Calculate available height for table ──
  useEffect(() => {
    const updateHeight = () => {
      if (tableWrapperRef.current) {
        const rect = tableWrapperRef.current.getBoundingClientRect();
        const availableHeight = window.innerHeight - rect.top - 60;
        if (availableHeight > 200) {
          setTableScrollHeight(`${availableHeight}px`);
        }
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    const timer = setTimeout(updateHeight, 300);
    return () => {
      window.removeEventListener("resize", updateHeight);
      clearTimeout(timer);
    };
  }, [projectCompounds]);

  const assayNameDropdownTemplate = (option) => {
    if (option.value === "__other__") {
      return <div className="flex align-items-center gap-2"><i className="pi pi-pencil text-gray-400" /> <span>Custom...</span></div>;
    }
    return <span>{option.label}</span>;
  };

  // ─── Main Render ───────────────────────────────────────────────

  if (loadingProjectCompounds) {
    return <SkeletonTable />;
  }

  return (
    <div className="flex flex-column w-full fadein animation-duration-500">
      <Toast ref={toast} />

      {/* ── BreadCrumb ── */}
      <div className="flex w-full pb-2">
        <BreadCrumb model={breadCrumbItems} />
      </div>

      <div className="flex w-full align-items-center mb-0.5">
        <SectionHeading
          icon="icon icon-common icon-math"
          heading="Compounds"
          color="#1e1b4b"
          textColor="#ffffff"
          customButtons={[
            { label: "Add Compound", icon: "pi pi-plus", action: () => setDisplayAddCompound(true), className: "p-button-sm add-compound-btn" },
            { label: "Manage Columns", icon: "pi pi-cog", action: () => openColumnDialog(), className: "p-button-sm add-compound-btn" },
            { label: "Import CSV", icon: "pi pi-upload", action: () => csvInputRef.current?.click(), className: "p-button-sm add-compound-btn" },
            { label: "", icon: "pi pi-ellipsis-h", action: (e) => moreMenuRef.current.toggle(e), className: "p-button-sm more-menu-btn" },
          ]}
        />
        {/* Hidden file input for CSV import */}
        <input type="file" accept=".csv" ref={csvInputRef} onChange={handleCsvFile} style={{ display: "none" }} />
        {/* Batch mode Menu */}
        <Menu
          model={[
            {
              label: batchMode ? "● Batch Mode" : "○ Batch Mode",
              icon: batchMode ? "pi pi-check-circle" : "pi pi-circle",
              command: () => {
                setBatchMode(!batchMode);
                if (batchMode) setSelectedCompounds([]);
              },
            },
            ...(batchMode && selectedCompounds.length > 0
              ? [
                  { separator: true },
                  {
                    label: `Delete (${selectedCompounds.length})`,
                    icon: "pi pi-trash",
                    command: () => setShowBatchDeleteDialog(true),
                    className: "p-menuitem-danger",
                  },
                ]
              : []),
          ]}
          ref={moreMenuRef}
          popup
          style={{ minWidth: "12rem" }}
          id="more-menu"
        />
      </div>

      <div ref={tableWrapperRef} className="flex w-full" style={{ flex: 1, minHeight: 0 }}>
        <DataTable
          value={localRows}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50, 100]}
          className="w-full project-compounds-table"
          emptyMessage="No compounds found for this project."
          stripedRows
          size="small"
          loading={loadingProjectCompounds}
          resizableColumns
          columnResizeMode="expand"
          scrollable
          scrollHeight={tableScrollHeight}
          responsiveLayout="scroll"
          selection={batchMode ? selectedCompounds : []}
          onSelectionChange={(e) => batchMode && setSelectedCompounds(e.value)}
        >
          {batchMode && <Column selectionMode="multiple" headerStyle={{ width: "3em" }} />}
          <Column
            header="Design ID"
            body={(rowData, options) => {
              const idx = options.rowIndex;
              if (editingIndex === idx) {
                return (
                  <InputText
                    value={rowData.designId || ""}
                    onChange={(e) => updateField(idx, "designId", e.target.value)}
                    style={{ width: "100%", fontSize: "13px" }}
                  />
                );
              }
              const display = rowData.designId || rowData.externalCompoundIds || "-";
              return <span className="font-semibold" style={{ fontSize: "13px", color: "#1e293b", lineHeight: 1.3 }}>{display}</span>;
            }}
            sortable
            sortField="designId"
            style={{ minWidth: "140px" }}
          />
          <Column
            header="SMILES*"
            field="smile"
            body={(rowData, options) => {
              const idx = options.rowIndex;
              if (editingIndex === idx) {
                return (
                  <InputText
                    value={rowData.smile || ""}
                    onChange={(e) => updateField(idx, "smile", e.target.value)}
                    style={{ width: "100%", fontSize: "12px", fontFamily: "monospace" }}
                  />
                );
              }
              return (
                <div className="flex align-items-center">
                  <code style={{
                    fontSize: "11px", color: "#64748b", background: "#f1f5f9",
                    padding: "2px 6px", borderRadius: 4, maxWidth: 150,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block",
                    fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                  }}>
                    {rowData.smile || <span style={{ color: "#cbd5e1" }}>-</span>}
                  </code>
                </div>
              );
            }}
            style={{ minWidth: "150px" }}
          />
          <Column
            header="Structure"
            body={(rowData) => {
              if (!rowData.smile) return <span style={{ color: "#cbd5e1" }}>—</span>;
              return (
                <div style={{ position: "relative", width: "100%", height: rowHeight, minHeight: rowHeight }}>
                  <div style={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center", cursor: "pointer" }}
                    onClick={() => { setStructurePreviewSmiles(rowData.smile); setDisplayStructurePreview(true); }}>
                    <canvas
                      id={`patent-structure-${rowData.id || Math.random()}`}
                      className="patent-structure-canvas"
                      data-smiles={rowData.smile}
                      style={{ display: "block" }}
                    />
                  </div>
                  <div
                    className="row-resize-handle"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const startY = e.clientY;
                      const startH = rowHeight;
                      const onMove = (ev) => {
                        const newH = Math.max(50, Math.min(300, startH + ev.clientY - startY));
                        setRowHeight(newH);
                      };
                      const onUp = () => {
                        document.removeEventListener('mousemove', onMove);
                        document.removeEventListener('mouseup', onUp);
                      };
                      document.addEventListener('mousemove', onMove);
                      document.addEventListener('mouseup', onUp);
                    }}
                  />
                </div>
              );
            }}
            style={{ minWidth: 90, width: 110 }}
          />
          <Column
            header="MW"
            field="molWeight"
            sortable
            body={(row) => row.molWeight ? <span className="font-semibold" style={{ fontSize: "13px", color: "#334155" }}>{parseFloat(row.molWeight).toFixed(1)}</span> : <span style={{ color: "#cbd5e1" }}>-</span>}
            style={{ minWidth: "80px" }}
          />
          <Column
            header="LogP"
            field="molLogP"
            sortable
            body={(row) => row.molLogP ? <span className="font-semibold" style={{ fontSize: "13px", color: "#334155" }}>{parseFloat(row.molLogP).toFixed(1)}</span> : <span style={{ color: "#cbd5e1" }}>-</span>}
            style={{ minWidth: "80px" }}
          />
          <Column
            header="SYNTHESIZED"
            field="synthesized"
            sortable
            body={(row, options) => {
              const idx = options.rowIndex;
              if (editingIndex === idx) {
                return (
                  <Dropdown
                    value={row.synthesized || ""}
                    options={[
                      { label: "YES", value: "YES" },
                      { label: "NO", value: "NO" },
                      { label: "Undergoing", value: "Undergoing" },
                      { label: "Failed", value: "Failed" },
                    ]}
                    onChange={(e) => updateField(idx, "synthesized", e.value)}
                    placeholder="Select"
                    style={{ width: "100%", minWidth: 100 }}
                  />
                );
              }
              const val = row.synthesized;
              if (!val) return <span style={{ fontSize: "11px", color: "#cbd5e1" }}>—</span>;
              const colors = { YES: "#22c55e", NO: "#ef4444", Undergoing: "#f59e0b", Failed: "#94a3b8" };
              return (
                <span style={{
                  fontSize: "11px", fontWeight: 600, color: colors[val] || "#64748b",
                  background: `${colors[val] || "#64748b"}18`, padding: "2px 8px", borderRadius: 4,
                  whiteSpace: "nowrap",
                }}>
                  {val}
                </span>
              );
            }}
            style={{ minWidth: "110px" }}
          />
          <Column
            header="ENTITY ID"
            field="entityId"
            sortable
            body={(row, options) => {
              const idx = options.rowIndex;
              if (editingIndex === idx) {
                return (
                  <InputText
                    value={row.entityId || ""}
                    onChange={(e) => updateField(idx, "entityId", e.target.value)}
                    style={{ width: "100%", fontSize: "12px" }}
                  />
                );
              }
              return row.entityId
                ? <span style={{ fontSize: "13px", fontWeight: 600, color: "#6366f1" }}>{row.entityId}</span>
                : <span style={{ fontSize: "11px", color: "#cbd5e1" }}>—</span>;
            }}
            style={{ minWidth: "110px" }}
          />
          {/* ── ExtraData custom columns (sub-dataset only) ── */}
          {/** Show extra columns when viewing any dataset (synthesized or custom), but not "All" view */}
          {dsParam && dsParam !== "all" && compoundColumns && compoundColumns.length > 0 && compoundColumns.map((col) => {
            return (
              <Column
                key={col.columnName}
                field={col.columnName}
                header={col.displayName || col.columnName}
                body={(rowData) => {
                  const val = rowData.extra?.[col.columnName];
                  if (val == null || val === "") return <span style={{ color: "#cbd5e1" }}>—</span>;
                  if (col.dataType === "number") {
                    const num = parseFloat(val);
                    return <span style={{ fontSize: "13px", color: "#334155", fontWeight: 600 }}>{isNaN(num) ? val : num.toFixed(2)}</span>;
                  }
                  return <span style={{ fontSize: "13px", color: "#334155" }}>{val}</span>;
                }}
                sortable
                style={{ minWidth: 120 }}
              />
            );
          })}
          <Column
            header={<span className="flex align-items-center gap-1"><i className="pi pi-chart-bar" style={{ fontSize: "12px", color: "#64748b" }} /><span>Activities</span></span>}
            body={activityCountTemplate}
            style={{ width: "110px", textAlign: "center" }}
          />
          <Column
            header="Actions"
            body={actionTemplate}
            style={{ minWidth: 80, width: 80, maxWidth: 80 }}
          />
        </DataTable>
      </div>

      {/* ── Column Management Dialog ── */}
      <Dialog
        header="Manage Columns"
        visible={displayColumnDialog}
        style={{ width: 500 }}
        modal
        onHide={() => setDisplayColumnDialog(false)}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setDisplayColumnDialog(false)} />
            <Button label="Save Columns" icon="pi pi-check" onClick={handleSaveColumns} />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          {editingColumns.map((col, i) => (
            <div key={i} className="flex align-items-center gap-2">
              <InputText
                value={col.displayName}
                onChange={(e) => {
                  const updated = [...editingColumns];
                  updated[i] = { ...updated[i], displayName: e.target.value };
                  setEditingColumns(updated);
                }}
                placeholder="Display name"
                style={{ flex: 1 }}
              />
              <Dropdown
                value={col.dataType}
                options={[
                  { label: "Text", value: "string" },
                  { label: "Number", value: "number" },
                  { label: "Date", value: "date" },
                ]}
                onChange={(e) => {
                  const updated = [...editingColumns];
                  updated[i] = { ...updated[i], dataType: e.value };
                  setEditingColumns(updated);
                }}
                style={{ width: 120 }}
              />
              <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" onClick={() => removeCustomColumn(i)} />
            </div>
          ))}
          <div className="flex align-items-center gap-2 pt-2" style={{ borderTop: "1px solid #e2e8f0" }}>
            <InputText
              value={newColumnName}
              onChange={(e) => setNewColumnName(e.target.value)}
              placeholder="New column name"
              style={{ flex: 1 }}
              onKeyDown={(e) => e.key === "Enter" && addCustomColumn()}
            />
            <Button label="Add" icon="pi pi-plus" className="p-button-sm" onClick={addCustomColumn} />
          </div>
        </div>
      </Dialog>

      {/* ── Batch Delete Confirm ── */}
      <Dialog
        header="Delete Compounds"
        visible={showBatchDeleteDialog}
        onHide={() => setShowBatchDeleteDialog(false)}
        style={{ width: "380px" }}
        modal
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button label="Cancel" className="p-button-text" onClick={() => setShowBatchDeleteDialog(false)} />
            <Button
              label={`Delete (${selectedCompounds.length})`}
              icon="pi pi-trash"
              className="p-button-danger"
              onClick={() => selectedCompounds.length > 0 && handleBatchDelete()}
            />
          </div>
        }
      >
        <p style={{ margin: 0, lineHeight: 1.6 }}>
          Are you sure you want to delete <strong>{selectedCompounds.length}</strong> compound(s)?<br />
          <span className="text-sm text-500">This action can be undone from the system admin.</span>
        </p>
      </Dialog>

      {/* ── Add Compound Sidebar ── */}
      <Sidebar
        visible={displayAddCompound}
        position="right"
        style={{ width: "36em", overflowX: "auto" }}
        onHide={() => setDisplayAddCompound(false)}
      >
        <div className="flex align-items-center gap-2 mb-3">
          <i className="pi pi-plus-circle text-primary" style={{ fontSize: "1.2rem" }} />
          <span className="font-bold text-lg">Add Compound</span>
          {selectedProject?.projectName && (
            <span className="text-sm text-gray-400 ml-auto">{selectedProject.projectName}</span>
          )}
        </div>
        <hr />
        <div className="text-xs text-gray-500 mb-3">
          <i className="pi pi-info-circle mr-1" />
          Stage: <strong>{selectedProject?.currentStage || "current"}</strong>
        </div>
        <CompoundEvolutionAddNew
          closeSidebar={() => {
            setDisplayAddCompound(false);
            fetchCompoundsByProject(projectId);
          }}
        />
      </Sidebar>

      {/* ── Add Activity Dialog ── */}
      <Dialog
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-chart-bar" style={{ fontSize: "14px", color: "#6366f1" }} />
            <span style={{ fontSize: "14px", fontWeight: 600 }}>New Activity</span>
            {activeCompoundForActivity
              ? <span style={{ fontSize: "12px", color: "#94a3b8", fontWeight: 400 }}>
                  — {activeCompoundForActivity.externalCompoundIds
                    || (activeCompoundForActivity.smile
                      ? activeCompoundForActivity.smile.substring(0, 20) + "..."
                      : "compound")}
                </span>
              : null}
          </div>
        }
        visible={displayAddActivity}
        style={{ width: "600px" }}
        modal
        onHide={() => setDisplayAddActivity(false)}
        footer={
          <div className="flex gap-2 justify-content-end" style={{ padding: "4px 0" }}>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setDisplayAddActivity(false)}
            />
            <Button
              label="Add Activity"
              icon="pi pi-check"
              style={{
                background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                border: "none", color: "#fff",
                borderRadius: 6, padding: "8px 16px",
                boxShadow: "0 2px 6px rgba(99,102,241,0.25)",
                fontWeight: 500,
              }}
              onClick={handleSubmitActivity}
            />
          </div>
        }
      >
        <div className="flex gap-4">
          {/* Left: SMILES Card */}
          <div style={{ width: "160px", flexShrink: 0 }}>
            {activeCompoundForActivity?.smile ? (
              <div style={{
                borderRadius: 8, overflow: "hidden",
                border: "1px solid #e2e8f0", background: "#fff",
                padding: "8px",
              }}>
                <SmilesView smiles={activeCompoundForActivity.smile} width={140} height={120} />
              </div>
            ) : (
              <div style={{
                width: 140, height: 120, borderRadius: 8,
                background: "#f8fafc", border: "1px dashed #e2e8f0",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="pi pi-image" style={{ fontSize: "2rem", color: "#cbd5e1" }} />
              </div>
            )}
            <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: 6, textAlign: "center" }}>
              Compound structure
            </div>
          </div>

          {/* Right: Form Fields */}
          <div className="flex-1">
            <div className="p-fluid">
            <div className="grid">
              <div className="field col-6">
                <label htmlFor="assayName">Assay Name *</label>
                <Dropdown
                  id="assayName"
                  value={activityForm.assayName}
                  options={ASSAY_OPTIONS}
                  onChange={(e) => setActivityForm({ ...activityForm, assayName: e.value })}
                  placeholder="Select or type..."
                  itemTemplate={assayNameDropdownTemplate}
                  filter
                  filterPlaceholder="Search assay..."
                />
              </div>
              <div className="field col-6">
                <label htmlFor="assayType">Assay Type</label>
                <Dropdown
                  id="assayType"
                  value={activityForm.assayType}
                  options={ASSAY_TYPE_OPTIONS}
                  onChange={(e) => setActivityForm({ ...activityForm, assayType: e.value })}
                  placeholder="Select type..."
                />
              </div>
            </div>
            {activityForm.assayName === "__other__" && (
              <div className="field">
                <label htmlFor="customAssayName">Custom Assay Name</label>
                <InputText
                  id="customAssayName"
                  value={customAssayName}
                  onChange={(e) => setCustomAssayName(e.target.value)}
                  placeholder="Enter custom assay name..."
                  autoFocus
                />
              </div>
            )}
            <div className="grid">
              <div className="field col-6">
                <label htmlFor="value">Value *</label>
                <InputText
                  id="value"
                  value={activityForm.value}
                  onChange={(e) => setActivityForm({ ...activityForm, value: e.target.value })}
                  placeholder="e.g. 8"
                />
              </div>
              <div className="field col-6">
                <label htmlFor="unit">Unit</label>
                <Dropdown
                  id="unit"
                  value={activityForm.unit}
                  options={[
                    { label: "µg/mL", value: "µg/mL" },
                    { label: "µM", value: "µM" },
                    { label: "nM", value: "nM" },
                    { label: "mg/L", value: "mg/L" },
                    { label: "%", value: "%" },
                    { label: "Other", value: "__custom_unit__" },
                  ]}
                  onChange={(e) => {
                    const val = e.value === "__custom_unit__" ? activityForm.unit || "" : e.value;
                    setActivityForm({ ...activityForm, unit: val });
                  }}
                  placeholder="Select unit..."
                  editable
                />
              </div>
            </div>
            <div className="grid">
              <div className="field col-6">
                <label htmlFor="method">Method</label>
                <Dropdown
                  id="method"
                  value={activityForm.method}
                  options={[
                    { label: "Broth microdilution", value: "Broth microdilution" },
                    { label: "Agar dilution", value: "Agar dilution" },
                    { label: "MTT assay", value: "MTT assay" },
                    { label: "CCK-8 assay", value: "CCK-8 assay" },
                    { label: "Surface Plasmon Resonance", value: "SPR" },
                    { label: "Isothermal Titration Calorimetry", value: "ITC" },
                    { label: "Fluorescence polarization", value: "Fluorescence polarization" },
                    { label: "Other", value: "__custom_method__" },
                  ]}
                  onChange={(e) => setActivityForm({ ...activityForm, method: e.value === "__custom_method__" ? "" : e.value })}
                  placeholder="Select method..."
                  editable
                />
              </div>
              <div className="field col-6">
                <label htmlFor="condition">Conditions</label>
                <InputText
                  id="condition"
                  value={activityForm.condition}
                  onChange={(e) => setActivityForm({ ...activityForm, condition: e.target.value })}
                  placeholder="e.g. pH 7.4, 37°C, 24h"
                />
              </div>
            </div>
            <div className="grid">
              <div className="field col-6">
                <label htmlFor="targetName">Target Name</label>
                <InputText
                  id="targetName"
                  value={activityForm.targetName}
                  onChange={(e) => setActivityForm({ ...activityForm, targetName: e.target.value })}
                  placeholder="e.g. InhA, CYP51, FabI"
                />
              </div>
              <div className="field col-6">
                <label htmlFor="pmid">PubMed ID (PMID)</label>
                <InputText
                  id="pmid"
                  value={activityForm.pmid}
                  onChange={(e) => setActivityForm({ ...activityForm, pmid: e.target.value })}
                  placeholder="e.g. 34567890"
                />
              </div>
            </div>
            <div className="field">
              <label htmlFor="notes">Notes</label>
              <InputTextarea
                id="notes"
                value={activityForm.notes}
                rows={2}
                onChange={(e) => setActivityForm({ ...activityForm, notes: e.target.value })}
                placeholder="Optional notes..."
              />
            </div>
          </div>
        </div>
      </div>
      </Dialog>

      {/* ── Edit Activity Dialog ── */}
      <Dialog
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-pencil" style={{ fontSize: "14px", color: "#6366f1" }} />
            <span style={{ fontSize: "14px", fontWeight: 600 }}>Edit Activity</span>
          </div>
        }
        visible={displayEditActivity}
        style={{ width: "600px" }}
        modal
        onHide={() => { setDisplayEditActivity(false); setEditingActivityRow(null); }}
        footer={
          <div className="flex gap-2 justify-content-end" style={{ padding: "4px 0" }}>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => { setDisplayEditActivity(false); setEditingActivityRow(null); }} />
            <Button label="Save Changes" icon="pi pi-check" style={{
              background: "linear-gradient(135deg, #6366f1, #4f46e5)",
              border: "none", color: "#fff", borderRadius: 6, padding: "8px 16px",
              boxShadow: "0 2px 6px rgba(99,102,241,0.25)", fontWeight: 500,
            }} onClick={handleEditActivity} />
          </div>
        }
      >
        <div className="flex gap-4">
          <div style={{ width: "160px", flexShrink: 0 }}>
            {editingActivityRow?.compound?.smile ? (
              <div style={{ borderRadius: 8, overflow: "hidden", border: "1px solid #e2e8f0", background: "#fff", padding: "8px" }}>
                <SmilesView smiles={editingActivityRow.compound.smile} width={140} height={120} />
              </div>
            ) : (
              <div style={{ width: 140, height: 120, borderRadius: 8, background: "#f8fafc", border: "1px dashed #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="pi pi-image" style={{ fontSize: "2rem", color: "#cbd5e1" }} />
              </div>
            )}
            <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: 6, textAlign: "center" }}>
              Compound structure
            </div>
          </div>
          <div className="flex-1">
            <div className="p-fluid">
              <div className="grid">
                <div className="field col-6">
                  <label>Assay Name *</label>
                  <Dropdown value={activityForm.assayName} options={ASSAY_OPTIONS} onChange={(e) => setActivityForm({ ...activityForm, assayName: e.value })} placeholder="Select or type..." itemTemplate={assayNameDropdownTemplate} filter filterPlaceholder="Search assay..." />
                </div>
                <div className="field col-6">
                  <label>Assay Type</label>
                  <Dropdown value={activityForm.assayType} options={ASSAY_TYPE_OPTIONS} onChange={(e) => setActivityForm({ ...activityForm, assayType: e.value })} placeholder="Select type..." />
                </div>
              </div>
              {activityForm.assayName === "__other__" && (
                <div className="field">
                  <label>Custom Assay Name</label>
                  <InputText value={customAssayName} onChange={(e) => setCustomAssayName(e.target.value)} placeholder="Enter custom..." autoFocus />
                </div>
              )}
              <div className="grid">
                <div className="field col-6">
                  <label>Value *</label>
                  <InputText value={activityForm.value} onChange={(e) => setActivityForm({ ...activityForm, value: e.target.value })} placeholder="e.g. 8" />
                </div>
                <div className="field col-6">
                  <label>Unit</label>
                  <Dropdown value={activityForm.unit} options={[{ label: "µg/mL", value: "µg/mL" }, { label: "µM", value: "µM" }, { label: "nM", value: "nM" }, { label: "mg/L", value: "mg/L" }, { label: "%", value: "%" }, { label: "Other", value: "__custom_unit__" }]} onChange={(e) => { const v = e.value === "__custom_unit__" ? activityForm.unit || "" : e.value; setActivityForm({ ...activityForm, unit: v }); }} placeholder="Select unit..." editable />
                </div>
              </div>
              <div className="grid">
                <div className="field col-6">
                  <label>Method</label>
                  <Dropdown value={activityForm.method} options={[{ label: "Broth microdilution", value: "Broth microdilution" }, { label: "Agar dilution", value: "Agar dilution" }, { label: "MTT assay", value: "MTT assay" }, { label: "CCK-8 assay", value: "CCK-8 assay" }, { label: "SPR", value: "SPR" }, { label: "ITC", value: "ITC" }, { label: "Fluorescence polarization", value: "Fluorescence polarization" }, { label: "Other", value: "__custom_method__" }]} onChange={(e) => setActivityForm({ ...activityForm, method: e.value === "__custom_method__" ? "" : e.value })} placeholder="Select method..." editable />
                </div>
                <div className="field col-6">
                  <label>Conditions</label>
                  <InputText value={activityForm.condition} onChange={(e) => setActivityForm({ ...activityForm, condition: e.target.value })} placeholder="e.g. pH 7.4, 37°C, 24h" />
                </div>
              </div>
              <div className="grid">
                <div className="field col-6">
                  <label>Target Name</label>
                  <InputText value={activityForm.targetName} onChange={(e) => setActivityForm({ ...activityForm, targetName: e.target.value })} placeholder="e.g. InhA, CYP51, FabI" />
                </div>
                <div className="field col-6">
                  <label>PubMed ID</label>
                  <InputText value={activityForm.pmid} onChange={(e) => setActivityForm({ ...activityForm, pmid: e.target.value })} placeholder="e.g. 34567890" />
                </div>
              </div>
              <div className="field">
                <label>Notes</label>
                <InputTextarea value={activityForm.notes} rows={2} onChange={(e) => setActivityForm({ ...activityForm, notes: e.target.value })} placeholder="Optional..." />
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        header={
          <div className="flex align-items-center gap-2" style={{ color: "#dc2626" }}>
            <i className="pi pi-exclamation-triangle" style={{ fontSize: "16px" }} />
            <span style={{ fontSize: "14px", fontWeight: 600 }}>Confirm Delete</span>
          </div>
        }
        visible={displayDeleteConfirm}
        style={{ width: "420px" }}
        modal
        onHide={() => { setDisplayDeleteConfirm(false); setDeletingActivityRow(null); }}
        footer={
          <div className="flex gap-2 justify-content-end" style={{ padding: "4px 0" }}>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => { setDisplayDeleteConfirm(false); setDeletingActivityRow(null); }} />
            <Button label="Delete" icon="pi pi-trash" className="p-button-danger" style={{ borderRadius: 6, fontWeight: 500 }} onClick={handleDeleteActivity} />
          </div>
        }
      >
        <div style={{ padding: "8px 0" }}>
          <p style={{ fontSize: "13px", color: "#475569", margin: "0 0 12px 0" }}>
            Are you sure you want to delete this activity record?
          </p>
          {deletingActivityRow && (
            <div style={{
              padding: "12px", background: "#fef2f2", borderRadius: 8,
              border: "1px solid #fecaca", fontSize: "12px", color: "#7f1d1d",
            }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{deletingActivityRow.assayName}</div>
              <div><strong>Value:</strong> {deletingActivityRow.value}{deletingActivityRow.unit ? " " + deletingActivityRow.unit : ""}</div>
              {deletingActivityRow.targetName && <div><strong>Target:</strong> {deletingActivityRow.targetName}</div>}
            </div>
          )}
        </div>
      </Dialog>

      {/* ── Structure Preview Sidebar ── */}
      <Dialog
        header="Molecular Structure"
        visible={displayStructurePreview}
        style={{ width: "480px" }}
        modal
        onHide={() => {
          setDisplayStructurePreview(false);
          setStructurePreviewSmiles(null);
        }}
        footer={
          <Button
            label="Close"
            icon="pi pi-times"
            className="p-button-text"
            onClick={() => {
              setDisplayStructurePreview(false);
              setStructurePreviewSmiles(null);
            }}
          />
        }
      >
        {structurePreviewSmiles && (
          <div className="flex justify-content-center p-3">
            <SmilesView smiles={structurePreviewSmiles} width={400} height={350} />
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default observer(ProjectCompounds);
